import { useState, useMemo, memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useOrganizationPagesOptimized as useOrganizationPages } from "@/hooks/admin/useOrganizationPagesOptimized";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useIsAdmin } from "@/hooks/auth/useIsAdmin";
import { useCanAccessAnalytics } from "@/hooks/auth/useCanAccessAnalytics";
import { LayoutDashboard, Users, GitBranch, CheckSquare, MessageCircle, MessageSquare, BarChart3, Settings, Mail, Building2, DollarSign, Workflow, Mic, Package, Target, Crosshair, Percent, Zap, PhoneCall, ChevronLeft, ChevronRight, ChevronDown, FileBarChart, Megaphone, Gauge, UserCog, Play, CalendarClock, CalendarPlus, FileSignature, HandCoins, FolderKanban, FileText, SquareKanban, PackageCheck, ListChecks, Timer, Ban, BookOpen
} from "lucide-react";
import { SidebarSkeleton } from "@/components/ui/sidebar-skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUnreadBadge } from "@/hooks/app/useUnreadBadge";
import { isSquadOrg } from "@/pages/relatorio-squad/squadConfig";
import { usePermissions } from "@/hooks/permissions/usePermissions";
import { routeToPermission } from "@/lib/permissions/catalog";
import { useOrgPreset } from "@/hooks/shared/useOrgPreset";
import { isNavItemActive } from "@/lib/orgPresets";
interface SidebarProps {
  className?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}
type NavItem = { name: string; href: string; icon: typeof LayoutDashboard; current: boolean };
type NavGroup = { id: string; label: string; icon: typeof LayoutDashboard; pinned?: boolean; items: NavItem[] };
const SidebarComponent = ({
  className,
  collapsed: externalCollapsed,
  onToggle
}: SidebarProps) => {
  const [internalCollapsed] = useState(true); // Colapsada por padrão
  const collapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
  // Colapsada = rail plano com o ícone de CADA item visível (não de seção), com
  // Tooltip no hover. Expandida (fixada) = acordeão por seção. Não há flyout — o
  // comentário anterior descrevia um desenho que nunca chegou ao código, e chegou a
  // induzir erro em documento de planejamento (30/08).
  const showText = !collapsed;

  const location = useLocation();
  const pagesHook = useOrganizationPages();
  const { currentOrg } = useOrganization();
  const orgPreset = useOrgPreset();
  const { isAdmin, isSuperAdmin } = useIsAdmin();
  const pagesLoading = useMemo(() => pagesHook.loading, [pagesHook.loading]);
  const { data: unreadCount } = useUnreadBadge();

  // Acordeão das seções — abertas por padrão (mapa guarda só as recolhidas).
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = useCallback((id: string) => {
    setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  /** Analytics só para admin, manager ou owner; rep não vê o item nem acessa a página */
  const { canAccessAnalytics } = useCanAccessAnalytics();

  // Permissões por grupo. ADITIVO no v1: entra em AND com a escada abaixo, nunca no lugar dela.
  // A escada codifica 6 tipos de regra em 20 ramos e só 7 são gate de papel; os outros 11
  // (hard-on/off, flag de org, UUID de org, isSquadOrg) respondem "a ORG tem isto?", que é outro
  // eixo. Enquanto `permsLoading`, `has()` devolve o catálogo vazio — por isso o guard abaixo.
  const { has: temPermissao, loading: permsLoading } = usePermissions();

  const canAccessSettings = isAdmin || isSuperAdmin;
  

  // Removed special layout - all pages now use consistent sidebar
  const needsSpecialLayout = false;

  // Removido handleToggle - sidebar agora expande apenas no hover
  // Navegação agrupada em seções (UX). Grupos com `pinned: true` renderizam
  // sem cabeçalho de acordeão (sempre abertos). Itens desabilitados por
  // permissão são filtrados depois em `visibleGroups`.
  const navGroupsPadrao = useMemo<NavGroup[]>(() => [
    {
      id: "inicio",
      label: "Início",
      icon: LayoutDashboard,
      items: [
        { name: "Dashboard", href: "/relatorio-squad", icon: LayoutDashboard, current: location.pathname.startsWith("/relatorio-squad") },
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, current: location.pathname === "/dashboard" },
        { name: "Atividades", href: "/activities", icon: CheckSquare, current: location.pathname === "/activities" || (location.pathname.startsWith("/activities") && !location.pathname.startsWith("/activities/focus")) },
        { name: "Execução focada", href: "/activities/focus", icon: Play, current: location.pathname.startsWith("/activities/focus") },
        { name: "Painel do Vendedor", href: "/vendedor", icon: Gauge, current: location.pathname.startsWith("/vendedor") },
      ],
    },
    {
      id: "clientes",
      label: "Vendas",
      icon: Users,
      items: [
        { name: "Leads", href: "/leads", icon: Users, current: location.pathname.startsWith("/leads") && !location.pathname.startsWith("/leads/segments") },
        { name: "Empresas", href: "/companies", icon: Building2, current: location.pathname.startsWith("/companies") },
        { name: "Negócios", href: "/deals", icon: DollarSign, current: location.pathname.startsWith("/deals") },
        { name: "Documentos", href: "/documentos", icon: FileSignature, current: location.pathname.startsWith("/documentos") },
        // Catálogo de produtos (em Vendas desde 09/10/2026; antes em Operações). Gate de PESSOA:
        // `clientes.produtos.view` (resolvido por `routeToPermission`); ver o ramo `/produtos` em
        // `stableIsRouteEnabled`.
        { name: "Produtos", href: "/produtos", icon: Package, current: location.pathname.startsWith("/produtos") },
      ],
    },
    {
      // Os cinco grupos originais eram todos PRE-venda: nao havia casa para o que
      // acontece depois do ganho. A leitura aqui e "vendi, agora entrego".
      //
      // As views sao ITENS do grupo, nao abas de um portal. Comecaram como aba porque a
      // Onda 1 tinha uma view so — com tres irmas, a barra de abas virava uma segunda
      // navegacao fazendo o trabalho da primeira, e "Operacoes" era o unico grupo da
      // sidebar com um filho unico. Playbooks (Onda 2) e Aprovacoes (3) entram aqui.
      id: "operacoes",
      label: "Operações",
      icon: FolderKanban,
      //
      // A estrutura e a do Ninho (docs/specs/modulo-operacoes-scrum.md): Dashboard · Quadro ·
      // Entregas · Tarefas · Sprints · Carteira. Meu trabalho, Projetos e Portfolios sairam
      // (as rotas redirecionam no portal). Cada item tem a sua chave `operacoes.<pagina>.view`,
      // resolvida por `routeToPermission` como qualquer outra rota.
      items: [
        { name: "Dashboard", href: "/operacoes/dashboard", icon: LayoutDashboard, current: location.pathname.startsWith("/operacoes/dashboard") },
        { name: "Quadro", href: "/operacoes/quadro", icon: SquareKanban, current: location.pathname.startsWith("/operacoes/quadro") },
        // Casa o workspace tambem (/operacoes/entregas/:id): ele PERTENCE a Entregas.
        { name: "Entregas", href: "/operacoes/entregas", icon: PackageCheck, current: location.pathname.startsWith("/operacoes/entregas") },
        { name: "Tarefas", href: "/operacoes/tarefas", icon: ListChecks, current: location.pathname.startsWith("/operacoes/tarefas") },
        { name: "Sprints", href: "/operacoes/sprints", icon: Timer, current: location.pathname.startsWith("/operacoes/sprints") },
        // "Carteira" = a base de contratos comerciais (client_contracts), no sentido que o
        // Nexus usa em `analise-carteira`: o conjunto que gera MRR. Nao confundir com
        // "Documentos" (assinatura ZapSign) nem com a carteira de LEADS do vendedor, que e
        // outro sentido da mesma palavra neste produto. A ROTA segue /operacoes/contratos:
        // ela sustenta o redirect de /contratos; a chave e `operacoes.carteira.view`.
        { name: "Carteira", href: "/operacoes/contratos", icon: FileText, current: location.pathname.startsWith("/operacoes/contratos") },
        // Esteira de cancelamento. Além da chave `operacoes.cancelamento.view`, exige a flag
        // `contract_lifecycle_enabled` da org (ver `stableIsRouteEnabled`).
        { name: "Cancelamento", href: "/operacoes/cancelamento", icon: Ban, current: location.pathname.startsWith("/operacoes/cancelamento") },
      ],
    },
    {
      id: "comunicacao",
      label: "Comunicação",
      icon: MessageCircle,
      items: [
        { name: "E-mail", href: "/email", icon: Mail, current: location.pathname.startsWith("/email") },
        { name: "Atendimento", href: "/chat", icon: MessageCircle, current: location.pathname.startsWith("/chat") },
        { name: "WhatsApp Oficial", href: "/whatsapp-oficial", icon: Megaphone, current: location.pathname.startsWith("/whatsapp-oficial") },
        { name: "SMS", href: "/sms", icon: MessageSquare, current: location.pathname.startsWith("/sms") },
        { name: "Discadora", href: "/discadora", icon: PhoneCall, current: location.pathname.startsWith("/discadora") },
        { name: "Vox AI", href: "/vox", icon: Mic, current: location.pathname.startsWith("/vox") },
      ],
    },
    {
      // Documentos que o time estuda (e que a IA do feedback usa como régua). Os "Playbooks" daqui
      // são documentos; os da Onda 2 de Operações são modelos de tarefa e entram lá em cima.
      id: "materiais",
      label: "Materiais",
      icon: BookOpen,
      items: [
        { name: "Playbooks", href: "/materiais/playbooks", icon: BookOpen, current: location.pathname.startsWith("/materiais/playbooks") },
      ],
    },
    {
      id: "automacao",
      label: "Automação",
      icon: Workflow,
      items: [
        { name: "Automações", href: "/automations", icon: Workflow, current: location.pathname.startsWith("/automations") },
        { name: "Cadências", href: "/sequences", icon: GitBranch, current: location.pathname.startsWith("/sequences") },
        { name: "Follow-ups IA", href: "/follow-ups", icon: Zap, current: location.pathname.startsWith("/follow-ups") },
      ],
    },
    {
      id: "desempenho",
      label: "Desempenho",
      icon: BarChart3,
      items: [
        { name: "Métricas e Metas", href: "/metas", icon: Crosshair, current: location.pathname === "/metas" },
        { name: "Relatórios personalizados", href: "/metricas", icon: BarChart3, current: location.pathname.startsWith("/metricas") },
        { name: "Relatório Midhaus", href: "/relatorio-midhaus", icon: FileBarChart, current: location.pathname.startsWith("/relatorio-midhaus") },
        { name: "Relatório Atticus", href: "/relatorio-atticus", icon: FileBarChart, current: location.pathname.startsWith("/relatorio-atticus") },
        { name: "Gestão de Agenda", href: "/gestao-agenda", icon: CalendarClock, current: location.pathname.startsWith("/gestao-agenda") },
        { name: "Agendamento", href: "/agendamento", icon: CalendarPlus, current: location.pathname.startsWith("/agendamento") },
        { name: "Lead Scoring", href: "/lead-scoring", icon: Target, current: location.pathname.startsWith("/lead-scoring") },
        { name: "Painel de Gestão", href: "/gestor", icon: UserCog, current: location.pathname.startsWith("/gestor") },
      ],
    },
    {
      // Financeiro reune as DUAS pontas da comissao: o que a org PAGA ao vendedor
      // (antes em Desempenho > Comissionamento) e o que ela RECEBE do cliente.
      // Spec: docs/specs/comissoes-recebidas-expectativa.md
      id: "financeiro",
      label: "Financeiro",
      icon: HandCoins,
      items: [
        { name: "Comissões pagas", href: "/financeiro/comissoes-pagas", icon: Percent, current: location.pathname.startsWith("/financeiro/comissoes-pagas") || location.pathname.startsWith("/comissionamento") },
        { name: "Comissões recebidas", href: "/financeiro/comissoes-recebidas", icon: HandCoins, current: location.pathname.startsWith("/financeiro/comissoes-recebidas") },
      ],
    },
  ], [location.pathname]);

  // Org com preset de interface enxuta (Atticus.hub etc.): o menu inteiro vem do `shell` do
  // preset, e os gates de papel/permissão abaixo continuam valendo por cima. Preset só de marca
  // (`shell: null`) fica com o menu padrão. Ver src/lib/orgPresets.ts.
  const presetShell = orgPreset?.shell ?? null;
  const navGroups = useMemo<NavGroup[]>(() => {
    if (!presetShell) return navGroupsPadrao;
    return presetShell.navGroups.map(group => ({
      id: group.id,
      label: group.label,
      icon: group.icon,
      items: group.items.map(item => ({
        name: item.name,
        href: item.href,
        icon: item.icon,
        current: isNavItemActive(location.pathname, item.href),
      })),
    }));
  }, [presetShell, navGroupsPadrao, location.pathname]);

  // Durante o loading inicial, o hook já mantém as rotas padrão (DEFAULT_ENABLED_ROUTES)
  // ou o último cache válido em pagesHook.enabledRoutes. Não duplicar uma lista
  // hardcoded aqui — qualquer divergência aparece como "página piscando/sumindo"
  // na sidebar nos primeiros frames pós-mount.
  const stableEnabledRoutes = useMemo(
    () => pagesHook.enabledRoutes,
    [pagesHook.enabledRoutes]
  );
  
  // Criar função estável de isRouteEnabled que não muda após inicialização
  const METAS_METRICAS_ORG_ID = '9d298125-4eb6-4c94-9168-b619bfc16b6a';
  // Org "Atticus" — relatório próprio em /relatorio-atticus (cópia do da Midhaus).
  const ATTICUS_ORG_ID = '08ae88ea-ca08-43fc-88f0-8c67b54783fd';

  const stableIsRouteEnabled = useCallback((route: string) => {
    const squadOrg = isSquadOrg(currentOrg?.id);
    // /agentes so aparece no menu das orgs com preset (no Vertto padrao a IA vive em
    // Configuracoes › IA). Sem chave no catalogo de permissoes e sem linha em `pages` de prod,
    // cairia no `includes` do final e sumiria — por isso o early return. Admin, porque e tela
    // de configuracao da IA.
    if (route === '/agentes') {
      return Boolean(presetShell) && canAccessSettings;
    }
    if (route === '/lead-scoring' || route === '/vox') return false;
    if (route === '/discadora') return true; // Discadora Sonax disponível pra todas as orgs
    if (route === '/vendedor') return true; // Página do Vendedor disponível pra todas as orgs
    if (route === '/activities/focus') return true; // Execução focada disponível pra todas as orgs
    if (route === '/chat') return true; // Atendimento disponível pra todas as orgs (imune ao drift de seed de organization_pages e a falhas transientes do fetch de páginas)
    if (route === '/dashboard' && squadOrg) return false; // Squad: dashboard = Relatório Squad (oculto)
    if (route === '/gestor') return canAccessAnalytics; // Página do Gestor: manager/admin/owner
    if (route === '/settings') return canAccessSettings;
    if (route === '/metas') {
      if (squadOrg) return true; // Squad: metas p/ o Relatório Squad
      // Liberado para todas as orgs via flag por org (kill-switch), com o mesmo
      // gate de role do /metricas (admin/manager/owner). A const Midhaus-only
      // permanece pois /relatorio-midhaus e /gestao-agenda ainda a usam.
      return Boolean(currentOrg?.goal_metrics_enabled) && canAccessAnalytics;
    }
    if (route === '/relatorio-midhaus') {
      return Boolean(currentOrg && currentOrg.id === METAS_METRICAS_ORG_ID);
    }
    if (route === '/relatorio-atticus') {
      // Ramo explícito obrigatório: sem ele a rota cai no fallthrough do catálogo
      // `pages`/`organization_pages` e o item some para TODOS, sem erro.
      return Boolean(currentOrg && currentOrg.id === ATTICUS_ORG_ID);
    }
    if (route === '/relatorio-squad') {
      return squadOrg;
    }
    if (route === '/gestao-agenda') {
      // Liberado para todas as orgs. O eixo da PESSOA e a chave
      // `desempenho.gestao_agenda.manage`, avaliada em `isRouteVisible` e exigida na rota
      // pelo <RequirePermission> — a mesma nos dois. O eixo da ORG saiu daqui: o UUID fixo
      // escondia a página de qualquer org nova (caso Atticus, 15/09) mesmo com o grupo de
      // acesso já tendo a permissão.
      return true;
    }
    // Booking Links: o eixo da PESSOA saiu daqui e virou chave, avaliada em `isRouteVisible`
    // com a MESMA chave que a rota exige. Antes a escada pedia admin/manager/owner e a rota
    // aceitava tambem closer/pre-vendedor: o closer nao via o item e a URL abria.
    if (route === '/agendamento') return true;
    // /documentos e a antiga /contratos (pagina ZapSign) renomeada na Onda 1 do modulo
    // de Projetos. Ver docs/specs/modulo-projetos.md §7.
    //
    // A divergencia que este comentario dava como "decisao separada" FOI RESOLVIDA na main:
    // a rota trocou `ProtectedRoute` (isAdmin GLOBAL) por
    // `RequirePermission k="clientes.contratos.view"`, entao o gestor deixou de ver o item e
    // levar "Acesso negado" no clique. Este gate segue em `canAccessAnalytics` de proposito —
    // e o que a main tambem faz, porque a chave nova cobre o mesmo conjunto de pessoas
    // (medido la: zero barradas). Se um dia divergirem, o certo e o menu passar a ler a chave.
    if (route === '/documentos') return canAccessAnalytics; // gestores/admins; página gere o estado vazio
    // Produtos (Vendas, 09/10/2026): sem flag de org — quem vê é a chave `clientes.produtos.view`
    // (`isRouteVisible`). Early return obrigatório: `/produtos` não existe na tabela `pages`, e sem
    // ele o `includes` do final daria `false` e o item sumiria do menu para todos.
    if (route === '/produtos') return true;
    // Operacoes: flag por org (kill-switch, nasce desligada). O gate de PESSOA e a permissao
    // `operacoes.<pagina>.view`, aplicada em `isRouteVisible`. NAO reintroduzir
    // `canAccessAnalytics` aqui: ele barrava quem tem papel `rep` mas esta num grupo com
    // Operacoes (ex.: o grupo "Operacao"), que via o menu sumir mesmo com a permissao. Mesmo
    // raciocinio do Financeiro logo abaixo — papel e grupo sao eixos ortogonais.
    // A rota em si nao e gated — o portal mostra um EmptyState explicando o modulo
    // desabilitado, para nao parecer "nenhum projeto ainda".
    //
    // ⚠️ PREFIXO, nao igualdade, e isso NAO e preferencia de estilo. Nenhuma rota
    // `/operacoes%` existe na tabela `pages`, entao tudo que nao for pego por um early
    // return aqui cai no `includes` do final, recebe `false` e SOME DO MENU para todos —
    // sem erro, sem log, sem nada na tela. Com igualdade, foi o que aconteceria com
    // /operacoes/portfolios e /operacoes/meu-trabalho no minuto em que viraram itens; com
    // prefixo, Playbooks e Aprovacoes ja nascem cobertos.
    // Cancelamento: o eixo da org é a flag do ciclo de vida, além da do módulo. Sem este ramo a
    // página apareceria no menu de toda org com Operações, antes da flag ligar.
    if (route === '/operacoes/cancelamento') {
      return Boolean(currentOrg?.projects_enabled && currentOrg?.contract_lifecycle_enabled);
    }
    if (route.startsWith('/operacoes/')) {
      return Boolean(currentOrg?.projects_enabled);
    }
    // Materiais: flag por org (kill-switch). Prefixo pelo mesmo motivo de Operações: nenhuma rota
    // `/materiais%` existe em `pages`, e sem este ramo o item sumiria para todos. O gate de PESSOA
    // é `materiais.playbooks.view`, em `isRouteVisible`.
    if (route.startsWith('/materiais/')) {
      return Boolean(currentOrg?.playbooks_enabled);
    }
    // Financeiro: flag por org (kill-switch, nasce desligada). O gate de PESSOA saiu daqui de
    // proposito e virou permissao (`financeiro.comissoes_*.view`), aplicada logo abaixo em
    // `isRouteVisible`. Manter `canAccessAnalytics` no AND bloquearia um Supervisor com papel
    // `rep` — e Supervisor e CARGO, nao papel (user_role e admin|manager|owner|rep). Os dois
    // eixos sao ortogonais por construcao; ver o cabecalho de src/lib/permissions/catalog.ts.
    if (route === '/financeiro/comissoes-pagas') {
      return Boolean(currentOrg?.commissions_enabled);
    }
    if (route === '/financeiro/comissoes-recebidas') {
      return Boolean(currentOrg?.commissions_receivable_enabled);
    }
    if (route === '/metricas') return canAccessAnalytics;
    if (route === '/whatsapp-oficial') return canAccessAnalytics; // admin/manager/owner
    // /whatsapp-saude saiu do MENU (virou seção de Configurações › Canais e
    // integrações), mas a rota continua viva como deep link. Este early return
    // tem de ficar: a rota nunca entrou no catálogo `pages`, então sem ele
    // cairia no `includes` abaixo e daria `false` para todo mundo.
    if (route === '/whatsapp-saude') return canAccessAnalytics; // admin/manager/owner
    // /sms NÃO tem gate especial de propósito: a visibilidade é controlada em
    // Super-Admin → Páginas da organização, como as demais páginas do catálogo
    // (tabela `pages` + `organization_pages`). Cai no `includes` abaixo, e a
    // mesma página é o único kill-switch — a `sms-send` também a consulta.
    //
    // Lição de 26/08: a primeira versão usava uma flag própria em
    // `organizations`. Não funcionou, porque o OrganizationContext carrega
    // apenas um punhado de colunas dessa tabela — a flag chegava `undefined` e
    // o item desaparecia em silêncio. Ao adicionar gate por coluna de org aqui,
    // confira antes se ela está no SELECT do contexto.
return stableEnabledRoutes.includes(route);
  }, [stableEnabledRoutes, canAccessAnalytics, canAccessSettings, currentOrg, presetShell]);

  // Camada de permissão POR CIMA da escada. Dois eixos, ambos precisam ser verdade.
  // Rota sem chave declarada passa direto: 92 das ~110 rotas do router não estão no catálogo
  // (que cobre as 28 da sidebar), e negar por omissão apagaria a maior parte do app. O
  // fail-closed por omissão entra na Fase 3, junto com a retirada da escada.
  const isRouteVisible = useCallback((route: string) => {
    if (!stableIsRouteEnabled(route)) return false;
    if (permsLoading) return true;          // não pisca o menu enquanto carrega
    // ⚠️ Estas duas sao gateadas na ROTA pela chave de ACAO `desempenho.gestao_agenda.manage`
    // (<RequirePermission>), nao pela chave de pagina que `routeToPermission` devolve. Ler a
    // chave de pagina aqui recriaria a divergencia menu x rota. Para /gestao-agenda o eixo da
    // ORG continua na escada acima (so Midhaus) — sao perguntas diferentes e nao se fundem.
    if (route === '/agendamento' || route === '/gestao-agenda') {
      return temPermissao('desempenho.gestao_agenda.manage');
    }
    return temPermissao(routeToPermission(route));
  }, [stableIsRouteEnabled, permsLoading, temPermissao]);

  // Filtra itens por permissão dentro de cada grupo e descarta grupos vazios.
  const visibleGroups = useMemo(() =>
    navGroups
      .map(group => ({ ...group, items: group.items.filter(item => isRouteVisible(item.href)) }))
      .filter(group => group.items.length > 0),
    [navGroups, isRouteVisible]
  );
  
  // Memoizar itens de navegação inferiores
  const allBottomNavigationItems = useMemo(() => [
    {
      name: "Configurações",
      href: "/settings",
      icon: Settings,
      current: location.pathname.startsWith("/settings")
    }
  ], [location.pathname]);

  // ⚠️ `isRouteVisible`, NAO `stableIsRouteEnabled`. Este era o unico item do menu que pulava a
  // camada de permissao: /settings tem chave declarada no catalogo e ela nunca era avaliada,
  // entao o item aparecia para quem a rota nega no clique. Medido: 40 managers, 56 owners e 170
  // reps veriam "Configuracoes" e levariam "Acesso negado" ao entrar.
  const bottomNavigation = useMemo(() =>
    allBottomNavigationItems.filter(item => isRouteVisible(item.href)),
    [allBottomNavigationItems, isRouteVisible]
  );

  // Pin = sidebar fixa aberta (collapsed externo === false).
  const isPinned = externalCollapsed === false;
  // data-expanded controla a largura via CSS — só expande quando fixada/aberta.
  const widthExpanded = !collapsed;

  // Renderiza um item de navegação (link + badge de não-lidas + tooltip no modo recolhido).
  const renderNavItem = (item: NavItem) => {
    const isChatItem = item.href === '/chat';
    const showBadge = isChatItem && !!unreadCount && unreadCount > 0;
    const linkContent = (
      <Link
        to={item.href}
        aria-label={item.name}
        className={cn(
          "relative flex items-center rounded-full text-sm font-medium transition-all duration-300",
          "h-10",
          showText ? "justify-start px-3" : "justify-center px-1.5",
          item.current ? "sidebar-nav-item-active" : "sidebar-nav-item text-muted-foreground"
        )}
      >
        <item.icon className={cn(
          "h-5 w-5 flex-shrink-0",
          item.current && !showText ? "mx-auto" : ""
        )} />
        {/* Unread dot when sidebar collapsed */}
        {showBadge && !showText && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive" />
        )}
        <span
          className={cn(
            "text-sm whitespace-nowrap transition-all duration-300 overflow-hidden",
            showText ? "opacity-100 ml-3 w-auto" : "opacity-0 ml-0 w-0"
          )}
        >
          {item.name}
        </span>
        {/* Unread count when sidebar expanded */}
        {showBadge && showText && (
          <span className="ml-auto flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Link>
    );
    return !showText ? (
      <Tooltip key={item.name}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" className="z-[200]">{item.name}</TooltipContent>
      </Tooltip>
    ) : (
      <div key={item.name}>{linkContent}</div>
    );
  };

  return <TooltipProvider delayDuration={200}><div
    className={cn(
      "flex flex-col border-r border-border sidebar-gradient-bg sidebar-vertical-gradient sidebar-expandable",
      needsSpecialLayout ? "z-[10000]" : "",
      "fixed left-0 top-16 z-50 shadow-xl",
      className
    )}
    data-tour="global-sidebar"
    data-expanded={widthExpanded ? "true" : "false"}
    data-pinned={isPinned ? "true" : "false"}
    style={{
      background: 'linear-gradient(to bottom, hsl(0 0% 0%) 0%, hsl(280 54% 22%) 100%)',
      backgroundImage: 'linear-gradient(to bottom, hsl(0 0% 0%) 0%, hsl(280 54% 22%) 100%)',
      height: 'calc(100vh - 4rem)',
      maxHeight: 'calc(100vh - 4rem)',
      // Colapsada: overflow visível para os flyouts escaparem para a direita.
      overflow: collapsed ? 'visible' : 'hidden'
    }}
  >
      {/* Navigation */}
      <nav className={cn(
        "flex-1 min-h-0 py-2 pt-4",
        // Colapsada: rail de ícones de itens (com tooltip); rola se passar da altura.
        showText ? "space-y-1 px-3 overflow-y-auto" : "space-y-1 px-1.5 overflow-y-auto"
      )}>
        {pagesLoading ? (
          <SidebarSkeleton collapsed={collapsed} count={5} />
        ) : !showText ? (
          // Modo recolhido: ícone de cada item (sem resumir em categoria) +
          // tooltip do título no hover; divisor sutil entre grupos.
          visibleGroups.map((group, index) => (
            <div key={group.id} className="space-y-1">
              {index > 0 && <div className="sidebar-section-divider" aria-hidden="true" />}
              {group.items.map(renderNavItem)}
            </div>
          ))
        ) : (
          visibleGroups.map((group, index) => {
            // Cabeçalho de seção (acordeão) com label + chevron. O divisor separa
            // as seções (exceto a primeira). Ponto ciano = seção recolhida que
            // contém a rota ativa ("você está aqui").
            const isOpen = !collapsedGroups[group.id];
            const hasActive = group.items.some(it => it.current);
            return (
              <div key={group.id} className="mb-1">
                {index > 0 && <div className="sidebar-section-divider" aria-hidden="true" />}
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  className="sidebar-group-head flex items-center justify-between w-full px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.1em]"
                >
                  <span>{group.label}</span>
                  <span className="flex items-center gap-1.5">
                    {!isOpen && hasActive && (
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: "hsl(var(--sidebar-ring))", boxShadow: "0 0 6px 1px hsl(var(--sidebar-ring))" }}
                      />
                    )}
                    <ChevronDown className={cn(
                      "h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200",
                      !isOpen && "-rotate-90"
                    )} />
                  </span>
                </button>
                <div
                  className="space-y-1 overflow-hidden transition-all duration-200"
                  style={{
                    maxHeight: isOpen ? group.items.length * 44 + 8 : 0,
                    opacity: isOpen ? 1 : 0
                  }}
                >
                  {group.items.map(renderNavItem)}
                </div>
              </div>
            );
          })

        )}
      </nav>


      {/* Bottom Navigation */}
      <div className={cn(
        "border-t border-border py-3 space-y-3 bg-transparent relative flex-shrink-0",
        showText ? "px-3" : "px-1.5",
        needsSpecialLayout ? "z-[9999] shadow-lg mb-6" : "z-10"
      )}>
        {pagesLoading ? (
          <SidebarSkeleton collapsed={collapsed} count={2} />
        ) : showText ? (
          bottomNavigation.map(item => (
            <div key={item.name}>
              <Link
                to={item.href}
                aria-label={item.name}
                className={cn(
                  "relative flex items-center rounded-full text-sm font-medium transition-all duration-300 h-10 justify-start px-3",
                  item.current ? "sidebar-nav-item-active" : "sidebar-nav-item text-muted-foreground"
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm whitespace-nowrap ml-3">{item.name}</span>
              </Link>
            </div>
          ))
        ) : (
          // Colapsado: ícone do item com tooltip (sem flyout).
          bottomNavigation.map(renderNavItem)
        )}

        {/* Pin/Unpin toggle — disponível nos dois modos (é a única forma de fixar/abrir). */}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={isPinned ? "Desafixar sidebar" : "Fixar sidebar aberta"}
            aria-pressed={isPinned}
            title={isPinned ? "Desafixar sidebar" : "Fixar sidebar aberta"}
            className={cn(
              "sidebar-pin-toggle flex items-center justify-center rounded-full transition-all duration-200",
              showText ? "h-7 w-7 ml-auto" : "h-9 w-9 mx-auto"
            )}
          >
            {isPinned ? (
              <ChevronLeft className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {/* User Profile Section removed - moved to navbar */}
    </div></TooltipProvider>;
};

// Memoizar componente para evitar re-renders desnecessários
// Só re-renderiza se props mudarem (className, collapsed, onToggle)
export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  // Comparação customizada: só re-renderizar se props realmente mudarem
  return (
    prevProps.className === nextProps.className &&
    prevProps.collapsed === nextProps.collapsed &&
    prevProps.onToggle === nextProps.onToggle
  );
});